import { LayoutGrid, List, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ComponentFilter } from './componentInventoryUtils';
import { COMPONENTS_SEARCH_WIDTH } from './components-page-layout';

export type ComponentLayout = 'cards' | 'list';

type FilterCounts = Record<ComponentFilter, number>;

type ComponentsControlsProps = {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  activeFilter: ComponentFilter;
  onFilterChange: (filter: ComponentFilter) => void;
  filterCounts: FilterCounts;
  layout: ComponentLayout;
  onLayoutChange: (layout: ComponentLayout) => void;
};

const FILTER_OPTIONS: Array<{
  id: ComponentFilter;
  label: string;
  dotClass?: string;
}> = [
  { id: 'all', label: 'All' },
  {
    id: 'frontend',
    label: 'Frontend',
    dotClass: 'bg-[var(--inv-accent)]',
  },
  {
    id: 'backend',
    label: 'Backend',
    dotClass: 'bg-[var(--inv-text-muted)]',
  },
];

const LAYOUT_OPTIONS: Array<{
  id: ComponentLayout;
  label: string;
  icon: typeof LayoutGrid;
}> = [
  { id: 'cards', label: 'Cards', icon: LayoutGrid },
  { id: 'list', label: 'List', icon: List },
];

const ComponentsControls = ({
  searchQuery,
  onSearchChange,
  activeFilter,
  onFilterChange,
  filterCounts,
  layout,
  onLayoutChange,
}: ComponentsControlsProps) => {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex w-full flex-wrap items-center gap-x-5 gap-y-3 max-md:flex-col max-md:items-stretch">
        <label className={cn('relative min-w-0', COMPONENTS_SEARCH_WIDTH)}>
          <span className="sr-only">Search components, repos, or branches</span>
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--inv-text-muted)]"
            strokeWidth={1.75}
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search..."
            className="box-border h-10 w-full min-w-0 rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] py-2 pl-10 pr-4 text-[0.8125rem] text-[var(--inv-text)] shadow-[var(--inv-shadow)] outline-none transition-[border-color,box-shadow] placeholder:truncate placeholder:text-[var(--inv-text-muted)] focus:border-[var(--inv-border-strong)] focus:ring-2 focus:ring-[var(--inv-accent-ring)]"
          />
        </label>

        <div
          className="ml-auto flex shrink-0 flex-wrap items-center gap-2.5 max-md:ml-0 max-md:w-full"
          role="tablist"
          aria-label="Filter components">
          {FILTER_OPTIONS.map(option => {
            const count = filterCounts[option.id];
            const isActive = activeFilter === option.id;

            return (
              <Button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                variant={isActive ? 'default' : 'outline'}
                size="pill"
                onClick={() => onFilterChange(option.id)}
                className={cn(
                  'h-10 px-4 text-[0.8125rem] font-medium leading-none shadow-[var(--inv-shadow)] hover:translate-y-0',
                  isActive
                    ? 'shadow-[0_4px_12px_rgba(var(--inv-accent-rgb),0.22)]'
                    : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
                )}>
                {option.dotClass && !isActive ? (
                  <span
                    className={cn('h-1.5 w-1.5 rounded-full', option.dotClass)}
                    aria-hidden="true"
                  />
                ) : null}
                <span>
                  {option.label} {count}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      <div
        className="flex items-center gap-2"
        role="group"
        aria-label="Component layout">
        {LAYOUT_OPTIONS.map(option => {
          const isActive = layout === option.id;
          const Icon = option.icon;
          return (
            <Button
              key={option.id}
              type="button"
              aria-pressed={isActive}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => onLayoutChange(option.id)}
              className={cn(
                'h-9 gap-1.5 px-3.5 text-[0.8125rem] font-medium shadow-[var(--inv-shadow)] hover:translate-y-0',
                !isActive &&
                  'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
              )}>
              <Icon
                className="h-3.5 w-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              {option.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default ComponentsControls;
